import { LoadingBar, Popup } from '@approvalmax/ui';
import { errorHelpers } from '@approvalmax/utils';
import { useAtomValue } from 'jotai';
import { clearCacheStorage } from 'modules/data-providers';
import { useDispatch, useSelector } from 'modules/react-redux';
import { requestSelector } from 'pages/requestForm/selectors/pageSelectors';
import { memo, useCallback, useState } from 'react';
import { useUnmount } from 'react-use';
import { notificationService } from 'services/notification';
import { useCreateNetSuiteVendor } from 'shared/data/webApp/v1';

import { useResetVendorFormState, useVendorTemplateFieldSettings } from '../../hooks';
import { netSuiteVendorFieldValuesState } from '../../NetSuiteVendorRequestForm/NetSuiteVendorRequestForm.states';
import { NetSuiteVendorForm } from '../NetSuiteVendorForm/NetSuiteVendorForm';
import { getVendorTransfer, getVendorValidationErrors } from '../NetSuiteVendorForm/NetSuiteVendorForm.helpers';
import { messages } from './NetSuiteVendorCreationPopup.messages';
import { LoadingBarContainer, Root, StyledPopup } from './NetSuiteVendorCreationPopup.styles';
import { NetSuiteVendorCreationPopupProps } from './NetSuiteVendorCreationPopup.types';

const NetSuiteVendorCreationPopup = memo<NetSuiteVendorCreationPopupProps>((props) => {
    const { companyId, terms, currencies, requestSubsidiary, onSave } = props;

    const dispatch = useDispatch();

    const request = useSelector(requestSelector);
    const fieldValues = useAtomValue(netSuiteVendorFieldValuesState);
    const resetVendorFormState = useResetVendorFormState();

    const fieldSettings = useVendorTemplateFieldSettings();

    const { mutate: createNetSuiteVendor, isLoading: isLoadingCreateNetSuiteVendor } = useCreateNetSuiteVendor();

    const [showErrors, setShowErrors] = useState(false);

    useUnmount(() => {
        resetVendorFormState();
    });

    const onCreate = useCallback(async () => {
        const validationErrors = getVendorValidationErrors({
            fieldValues,
            fieldSettings,
        });

        if (validationErrors.length) {
            validationErrors.forEach((message) => {
                notificationService.showErrorToast(message);
            });

            setShowErrors(true);

            return;
        }

        if (!fieldValues) {
            return;
        }

        createNetSuiteVendor(
            {
                body: {
                    companyId,
                    vendor: getVendorTransfer(fieldValues),
                },
            },
            {
                onSuccess: (data) => {
                    if (data.vendor && onSave) {
                        onSave(data.vendor);
                        dispatch(
                            clearCacheStorage(
                                (storageId) => storageId === `requestOptions.netSuiteVendors.${request.id}`
                            )
                        );
                    }
                },
                onError: (error) => {
                    errorHelpers.captureException(error);
                },
            }
        );
    }, [fieldValues, fieldSettings, request.id, createNetSuiteVendor, companyId, onSave, dispatch]);

    return (
        <StyledPopup
            title={messages.popupTitle}
            buttons={
                <Popup.DefaultContent.Button
                    disabled={isLoadingCreateNetSuiteVendor}
                    execute={onCreate}
                    title={messages.createButton}
                >
                    {messages.createButton}
                </Popup.DefaultContent.Button>
            }
        >
            <>
                {isLoadingCreateNetSuiteVendor && (
                    <LoadingBarContainer>
                        <LoadingBar />
                    </LoadingBarContainer>
                )}

                <Root>
                    <NetSuiteVendorForm
                        companyId={companyId}
                        terms={terms}
                        currencies={currencies}
                        requestSubsidiary={requestSubsidiary}
                        showErrors={showErrors}
                    />
                </Root>
            </>
        </StyledPopup>
    );
});

export default NetSuiteVendorCreationPopup;
